/**
 * Pure helpers for the Quality Co-Pilot data-request thread (no React):
 * interleaving the requester↔recipient thread, the answer-first split that
 * pins the newest answer, the collapsed-head excerpt, and the attention sort.
 *
 * Kept out of DataRequestCard.tsx so the component file only exports
 * components (fast refresh) and so the ordering/pinning logic is unit-testable
 * without rendering.
 */
import type {
  DataRequestMessage,
  DataRequestResponse,
  DoorDataRequestRead,
} from '../../services/qcpDataRequests';

export type ThreadEntry =
  | { kind: 'message'; at: string; message: DataRequestMessage }
  | { kind: 'answer'; at: string; response: DataRequestResponse };

/** Requester follow-ups + recipient answers, interleaved oldest-first. */
export function buildThread(request: DoorDataRequestRead): ThreadEntry[] {
  const entries: ThreadEntry[] = [
    ...(request.messages ?? []).map(
      (message): ThreadEntry => ({ kind: 'message', at: message.created_at, message }),
    ),
    ...request.responses.map(
      (response): ThreadEntry => ({ kind: 'answer', at: response.created_at, response }),
    ),
  ];
  return entries.sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * Answer-first split of a request's thread: the NEWEST recipient answer is
 * pinned (rendered at the top of the expanded card, and excerpted in the
 * collapsed head); everything else keeps chronological order underneath —
 * including any requester follow-ups sent after that answer.
 */
export function splitThread(request: DoorDataRequestRead): {
  latestAnswer: DataRequestResponse | null;
  rest: ThreadEntry[];
} {
  const thread = buildThread(request);
  let latest: DataRequestResponse | null = null;
  for (const entry of thread) {
    if (entry.kind === 'answer') latest = entry.response;
  }
  const pinnedId = latest?.id;
  const rest = pinnedId
    ? thread.filter((e) => !(e.kind === 'answer' && e.response.id === pinnedId))
    : thread;
  return { latestAnswer: latest, rest };
}

/** One-line excerpt of an answer for the collapsed card head. */
export function answerExcerpt(answer: DataRequestResponse): string {
  const text = answer.body_text.replace(/\s+/g, ' ').trim();
  if (text) return text;
  const n = answer.attachments.length;
  if (n > 0) return `📎 ${n} attachment${n > 1 ? 's' : ''}`;
  return '(empty answer)';
}

/**
 * Display order within a group: requests with an answer the user has not seen
 * yet come first, then newest first.
 */
export const byAttention = (a: DoorDataRequestRead, b: DoorDataRequestRead) =>
  Number(b.answer_unseen) - Number(a.answer_unseen) ||
  b.created_at.localeCompare(a.created_at);

/** Key of a thread entry for React lists. */
export const threadEntryKey = (entry: ThreadEntry) =>
  entry.kind === 'answer' ? `a-${entry.response.id}` : `m-${entry.message.id}`;
